import React from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { FileText, Plus, TrendingUp, Users } from 'lucide-react'

interface ServicesQuickActionsProps {
  seoAuditMode: boolean
  onEnableSeoAudit: () => void
  serviceAreasOnly: boolean
  onToggleServiceAreasOnly: () => void
  statusFilter: 'all' | 'published' | 'draft' | 'archived'
  onSetStatusFilter: (value: 'all' | 'published' | 'draft' | 'archived') => void
  onAddService: () => void
}

export function ServicesQuickActions({
  seoAuditMode,
  onEnableSeoAudit,
  serviceAreasOnly,
  onToggleServiceAreasOnly,
  statusFilter,
  onSetStatusFilter,
  onAddService,
}: ServicesQuickActionsProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Quick Actions</CardTitle>
        <CardDescription>Common service management tasks and shortcuts.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="hidden sm:grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Button variant="outline" className="h-auto p-4 justify-start" onClick={onAddService}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                <Plus className="h-4 w-4" />
              </div>
              <div className="text-left">
                <div className="font-medium">Add Service</div>
                <div className="text-sm text-muted-foreground">Create new service page</div>
              </div>
            </div>
          </Button>

          <Button
            variant={seoAuditMode ? 'default' : 'outline'}
            className="h-auto p-4 justify-start"
            onClick={onEnableSeoAudit}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 text-green-600">
                <TrendingUp className="h-4 w-4" />
              </div>
              <div className="text-left">
                <div className="font-medium">SEO Audit</div>
                <div className="text-sm text-muted-foreground">Review SEO performance</div>
              </div>
            </div>
          </Button>

          <Button
            variant={serviceAreasOnly ? 'default' : 'outline'}
            className="h-auto p-4 justify-start"
            onClick={onToggleServiceAreasOnly}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100 text-purple-600">
                <Users className="h-4 w-4" />
              </div>
              <div className="text-left">
                <div className="font-medium">Service Areas</div>
                <div className="text-sm text-muted-foreground">Manage coverage areas</div>
              </div>
            </div>
          </Button>

          <Button
            variant={statusFilter === 'draft' ? 'default' : 'outline'}
            className="h-auto p-4 justify-start"
            onClick={() => onSetStatusFilter('draft')}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                <FileText className="h-4 w-4" />
              </div>
              <div className="text-left">
                <div className="font-medium">Content Review</div>
                <div className="text-sm text-muted-foreground">Review all content</div>
              </div>
            </div>
          </Button>
        </div>

        <div className="sm:hidden">
          <ScrollArea className="w-full">
            <div className="flex gap-3 pb-2">
              <Button variant="outline" className="min-w-[220px] h-auto p-3 justify-start" onClick={onAddService}>
                <div className="p-2 rounded-lg bg-blue-100 text-blue-600 mr-3">
                  <Plus className="h-4 w-4" />
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium leading-tight">Add Service</div>
                  <div className="text-xs text-muted-foreground">Create new service page</div>
                </div>
              </Button>
              <Button
                variant={seoAuditMode ? 'default' : 'outline'}
                className="min-w-[220px] h-auto p-3 justify-start"
                onClick={onEnableSeoAudit}
              >
                <div className="p-2 rounded-lg bg-green-100 text-green-600 mr-3">
                  <TrendingUp className="h-4 w-4" />
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium leading-tight">SEO Audit</div>
                  <div className="text-xs text-muted-foreground">Review SEO performance</div>
                </div>
              </Button>
              <Button
                variant={serviceAreasOnly ? 'default' : 'outline'}
                className="min-w-[220px] h-auto p-3 justify-start"
                onClick={onToggleServiceAreasOnly}
              >
                <div className="p-2 rounded-lg bg-purple-100 text-purple-600 mr-3">
                  <Users className="h-4 w-4" />
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium leading-tight">Service Areas</div>
                  <div className="text-xs text-muted-foreground">Manage coverage areas</div>
                </div>
              </Button>
              <Button
                variant={statusFilter === 'draft' ? 'default' : 'outline'}
                className="min-w-[220px] h-auto p-3 justify-start"
                onClick={() => onSetStatusFilter('draft')}
              >
                <div className="p-2 rounded-lg bg-orange-100 text-orange-600 mr-3">
                  <FileText className="h-4 w-4" />
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium leading-tight">Content Review</div>
                  <div className="text-xs text-muted-foreground">Review all content</div>
                </div>
              </Button>
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>
      </CardContent>
    </Card>
  )
}
